import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../Icon';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  danger?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 520,
  danger
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Mantém o modal montado durante a animação de saída (§1.5.5).
  const [render, setRender] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setRender(true);
      setClosing(false);
    } else if (render) {
      setClosing(true);
      const t = setTimeout(() => {
        setRender(false);
        setClosing(false);
      }, 130);
      return () => clearTimeout(t);
    }
  }, [open, render]);

  useEffect(() => {
    if (!render || closing) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    // Auto-foco no primeiro focável do dialog
    requestAnimationFrame(() => {
      const root = dialogRef.current;
      if (!root) return;
      const focusable = root.querySelector<HTMLElement>(FOCUSABLE);
      focusable?.focus();
    });
    return () => window.removeEventListener('keydown', h);
  }, [render, closing, onClose]);

  // Focus-trap: Tab/Shift+Tab ciclam dentro do dialog (§1.5.1).
  const onKeyDownTrap = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !root.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !root.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!render) return null;

  return (
    <div
      onMouseDown={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'var(--scrim)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '10vh 20px 20px',
        animation: closing ? 'fadeOut 0.12s var(--ease) forwards' : 'fadeIn 0.16s var(--ease)'
      }}
    >
      <div
        ref={dialogRef}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDownTrap}
        role="dialog"
        aria-modal="true"
        style={{
          width,
          maxWidth: '100%',
          background: 'var(--bg-1)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--shadow-2)',
          animation: closing
            ? 'scaleOut 0.12s var(--ease) forwards'
            : 'scaleIn 0.2s var(--ease)',
          maxHeight: '82vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {title && (
          <div
            style={{
              padding: '18px 20px 14px',
              borderBottom: '1px solid var(--border-soft)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: 16,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: danger ? 'var(--danger)' : 'var(--text-0)'
                }}
              >
                {danger && <Icon name="alert" size={17} />}
                {title}
              </h3>
              {subtitle && (
                <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>{subtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="focusable"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-2)',
                padding: 4,
                borderRadius: 6,
                display: 'flex',
                cursor: 'pointer'
              }}
            >
              <Icon name="x" size={18} />
            </button>
          </div>
        )}
        <div style={{ padding: '18px 20px', overflowY: 'auto' }}>{children}</div>
        {footer && (
          <div
            style={{
              padding: '14px 20px',
              borderTop: '1px solid var(--border-soft)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
